/**
 * Renders the Pareto "P: Quantity Breaks" discount badge (the .omg-price
 * markup the app already shows on PDP) on top of product cards, using the
 * campaign config the app embeds as a page-global `paretoMetafields` const
 * (declared via inline <script>, so it's a lexical global, NOT a property
 * on window — read it as a bare identifier, never window.paretoMetafields).
 *
 * This reads an undocumented, internal data shape from a third-party app.
 * It only handles campaign_type "one_discount" and treats a product as
 * eligible unless it appears in the campaign's product_exclude list — it
 * does NOT evaluate product/collection-scoped inclusion rules (product_get,
 * collection_get) because we have no confirmed example of that shape. If a
 * future campaign is scoped to specific products/collections instead of
 * "all products", this script may show the badge on ineligible cards.
 * Re-check after any Pareto app update.
 */
/* global paretoMetafields */
(function () {
  function parseScheduleDate(value, timezone) {
    if (!value) return null;
    var iso = value.replace(' ', 'T');
    if (timezone === 'Asia/Singapore') iso += '+08:00';
    var date = new Date(iso);
    return isNaN(date.getTime()) ? null : date;
  }

  function getParetoMetafields() {
    return typeof paretoMetafields !== 'undefined' ? paretoMetafields : undefined;
  }

  function getActiveOneDiscountOffers() {
    var metafields = getParetoMetafields();
    var data = metafields && metafields.oqb_app_data;
    if (!data || !Array.isArray(data.offers)) return [];

    var now = new Date();
    return data.offers.filter(function (offer) {
      if (offer.campaign_type !== 'one_discount') return false;
      if (Number(offer.status) !== 1 || Number(offer.schedule_status) !== 1) return false;

      var from = parseScheduleDate(offer.schedule_from, offer.schedule_timezone);
      var to = parseScheduleDate(offer.schedule_to, offer.schedule_timezone);
      if (from && now < from) return false;
      if (to && now > to) return false;

      return Array.isArray(offer.discount_rule) && offer.discount_rule.length > 0;
    });
  }

  function isProductExcluded(offer, productId) {
    var exclude = offer.product_exclude;
    if (!exclude || String(exclude.status) !== '1') return false;
    var list = exclude.product_list || [];
    return list.some(function (item) {
      return String(item.product_id) === String(productId);
    });
  }

  function getBaseTier(offer) {
    var rule =
      offer.discount_rule.find(function (r) {
        return r.active_tier;
      }) || offer.discount_rule[0];
    return rule && rule.quantity_break;
  }

  function computeDiscountedText(originalText, percent) {
    var match = originalText.match(/[\d.,]*\d/);
    if (!match) return null;

    var raw = match[0];
    var decimalMatch = raw.match(/[.,](\d{2})$/);
    var decimalChar = decimalMatch ? decimalMatch[0].charAt(0) : null;
    var normalized;

    if (decimalChar) {
      var lastIndex = raw.lastIndexOf(decimalChar);
      var intPart = raw.slice(0, lastIndex).replace(/[.,]/g, '');
      var decPart = raw.slice(lastIndex + 1);
      normalized = intPart + '.' + decPart;
    } else {
      normalized = raw.replace(/[.,]/g, '');
    }

    var value = parseFloat(normalized);
    if (isNaN(value)) return null;

    var discounted = (value * (100 - percent)) / 100;
    var discountedStr = discounted.toFixed(decimalChar ? 2 : 0);
    if (decimalChar && decimalChar !== '.') {
      discountedStr = discountedStr.replace('.', decimalChar);
    }

    return originalText.replace(raw, discountedStr);
  }

  function findVisiblePriceItem(priceEl) {
    if (!priceEl) return null;
    var selector = priceEl.classList.contains('price--on-sale')
      ? '.price__sale .price-item--sale.price-item--last'
      : '.price__regular .price-item--regular';
    return priceEl.querySelector(selector);
  }

  function renderBadge(marker, originalText, discountedText, percent) {
    marker.innerHTML =
      '<span class="omg-price">' +
      '<span class="omg-origin-price">' +
      originalText +
      '</span>' +
      '<span class="omg-price-percent">' +
      percent +
      '%</span>' +
      '<span class="omg-discount-price money">' +
      discountedText +
      '</span>' +
      '</span>';
  }

  function process() {
    var metafields = getParetoMetafields();

    var offers = getActiveOneDiscountOffers();

    if (!offers.length) return;

    document.querySelectorAll('.card__quantity-break-price:not([data-qb-done])').forEach(function (marker) {
      marker.setAttribute('data-qb-done', 'true');

      var productId = marker.getAttribute('data-qb-product-id');
      if (!productId) return;

      var offer = offers.find(function (o) {
        return !isProductExcluded(o, productId);
      });
      if (!offer) return;

      var tier = getBaseTier(offer);
      if (!tier || tier.discount_unit !== 'percent') return;

      var percent = parseFloat(tier.discount_value);
      if (!percent || percent <= 0) return;

      var priceEl = marker.previousElementSibling;
      var priceItem = findVisiblePriceItem(priceEl);
      if (!priceItem) return;

      var originalText = priceItem.textContent.trim();
      var discountedText = computeDiscountedText(originalText, percent);
      if (!discountedText) return;

      renderBadge(marker, originalText, discountedText, percent);
      priceEl.style.display = 'none';
    });
  }

  var scheduled = false;
  function scheduleProcess() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      process();
    });
  }

  document.addEventListener('DOMContentLoaded', scheduleProcess);
  document.addEventListener('shopify:section:load', scheduleProcess);

  new MutationObserver(scheduleProcess).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (document.readyState !== 'loading') scheduleProcess();
})();
